import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { FiLock, FiUnlock, FiExternalLink, FiShield, FiAlertCircle, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const TokenGatedContent = ({ block, theme }) => {
  const { walletAddress, isWalletConnected, signIn } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const requirements = block.data.requirements || { contractAddress: '', minBalance: 1, network: 'ethereum' };

  useEffect(() => {
    if (isWalletConnected && walletAddress) {
      checkAccess();
    }
  }, [isWalletConnected, walletAddress]);

  const checkAccess = async () => {
    setChecking(true);
    try {
      // Mock check - replace with actual contract verification
      const mockHasAccess = Math.random() > 0.5; // Simulate 50% chance of having access
      setHasAccess(mockHasAccess);
      if (mockHasAccess) {
        setShowContent(true);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setChecking(false);
    }
  };

  const handleConnect = async () => {
    signIn();
  };

  const renderLockedContent = () => (
    <div className={`p-8 rounded-xl border ${theme.border} ${theme.bg}`}>
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiLock} className="text-red-600 text-2xl" />
        </div>
        <h3 className={`text-xl font-bold ${theme.text} mb-2`}>
          Token-Gated Content
        </h3>
        <p className={`${theme.subtext} mb-6`}>
          Connect your wallet and hold the required NFT to unlock this exclusive content
        </p>
        {!isWalletConnected ? (
          <button
            onClick={handleConnect}
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiShield} className="mr-2" />
            Connect Wallet
          </button>
        ) : (
          <div className="space-y-4">
            {checking ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                <span className={`${theme.subtext}`}>Checking access...</span>
              </div>
            ) : (
              <>
                {hasAccess === false && (
                  <div className={`p-4 rounded-lg bg-red-50 border border-red-200`}>
                    <div className="flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="text-red-600 mr-2" />
                      <span className="text-red-800 text-sm">
                        You don't own the required NFT to access this content
                      </span>
                    </div>
                  </div>
                )}
                <button
                  onClick={checkAccess}
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                >
                  <SafeIcon icon={FiShield} className="mr-2" /> Check Access
                </button>
              </>
            )}
          </div>
        )}
        {/* Requirements Display */}
        <div className={`mt-6 p-4 rounded-lg ${theme.bg} ${theme.border} border`}>
          <h4 className={`font-medium ${theme.text} mb-2`}>Requirements:</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Contract:</span>
              <span className={`font-mono ${theme.text}`}>
                {requirements.contractAddress.slice(0, 6)}...{requirements.contractAddress.slice(-4)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Minimum Balance:</span>
              <span className={`${theme.text}`}>{requirements.minBalance} NFT(s)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Network:</span>
              <span className={`${theme.text}`}>{requirements.network}</span>
            </div>
          </div>
          {requirements.contractAddress && (
            <a
              href={`https://opensea.io/assets/${requirements.contractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 text-indigo-600 hover:text-indigo-700 text-sm"
            >
              <SafeIcon icon={FiExternalLink} className="mr-1" /> View Collection
            </a>
          )}
        </div>
      </div>
    </div>
  );

  const renderUnlockedContent = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-xl border-2 border-green-500 ${theme.bg}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiUnlock} className="text-green-600 text-sm" />
          </div>
          <h3 className={`font-medium ${theme.text}`}>Exclusive Content Unlocked</h3>
        </div>
        <SafeIcon icon={FiCheck} className="text-green-600 text-xl" />
      </div>
      {/* Render the actual gated content */}
      <div className={`${theme.subtext} space-y-4`}>
        {block.data.contentType === 'text' && (
          <div>
            <h4 className={`font-medium ${theme.text} mb-2`}>{block.data.title}</h4>
            <div className="whitespace-pre-wrap">{block.data.content}</div>
          </div>
        )}
        {block.data.contentType === 'link' && (
          <a
            href={block.data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} className="mr-2" />
            {block.data.cta || 'Access Exclusive Content'}
          </a>
        )}
        {block.data.contentType === 'download' && (
          <a
            href={block.data.downloadUrl}
            download
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} className="mr-2" /> Download Exclusive File
          </a>
        )}
        {block.data.contentType === 'discount' && (
          <div className={`p-4 bg-yellow-50 border border-yellow-200 rounded-lg`}>
            <h4 className={`font-medium ${theme.text} mb-2`}>Exclusive Discount Code</h4>
            <p className={`text-2xl font-bold text-yellow-800 font-mono`}>{block.data.code}</p>
            <p className={`text-sm text-yellow-700 mt-2`}>{block.data.description}</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  if (showContent && hasAccess) {
    return renderUnlockedContent();
  }

  return renderLockedContent();
};

export default TokenGatedContent;